import { Member } from '@/features/iam/members/Member'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { action, computed, observable } from 'mobx'
import { EMPTY, type Observable, Subject } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators'
import { fromStream, toStream } from '@/utils/mobx-util'
import {
  filterNonNil,
  shareLatest,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type TeamSelected, TeamSelection } from './DraftPersister'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import { type StepFactory, type StepViewModel } from './OutlineViewModel'
import type {
  ListUserTeamMembersForWorkspaceFn,
  GetCurrentMemberFn,
  GetTeamMemberFn,
  GetTeamMembersForWorkspaceMemberFn,
} from '@/features/iam'
import { Team } from '@/features/iam/teams/Team'
import { type Id } from '@/infra/id'

const TITLE = 'Who would you like to submit this work request for?'

const ADMIN_TITLE = 'Should this job be owned by a team?'

/**
 * Team Selector view model, for selecting the team
 * to submit a work request for.
 */
export class TeamSelectorViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether `prepare` has completed since activation.
   */
  @observable private accessor _isReady = false

  private readonly _isReady$ = toStream(() => this._isReady, true)

  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly _changesSubject = new Subject<TeamSelected>()

  /**
   * Changes stream.
   */
  readonly changes$ = this._changesSubject.asObservable()

  /**
   * The selected team.
   */
  @observable private accessor _selectedTeam: TeamSelection | null = null

  /**
   * Emits the selected Team (`TeamSelection.myself` included) on subscribe,
   * but only after `prepare` has been run.
   */
  readonly selectedTeam$ = toStream(() => this.selectedTeam, true).pipe(
    filterNonNil(),
    distinctUntilChanged(),
    shareLatest(),
    whileActive(this._isReady$),
  )

  /**
   * Streams as MobX Observables.
   */
  private readonly _$

  /**
   * Constructor.
   *
   * @param createStep Factory for creating a Step.
   * @param member$ Observable for the selected workspace member
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _canEditDraftJobsAsAdmin Getter for whether the selector is visible
   */
  constructor(
    createStep: StepFactory,
    private readonly _member$: Observable<Member | null>,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getTeamMember: GetTeamMemberFn,
    private readonly _listUserTeamMembersForWorkspace: ListUserTeamMembersForWorkspaceFn,
    private readonly _getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _isDisabled: () => boolean,
    private readonly _canEditDraftJobsAsAdmin: () => boolean,
  ) {
    this._$ = {
      member: fromStream(Member.none, this._member$),
    }

    // Fetch user teams on WS change; select the initial
    // team after that happens.
    this._lifecycle.manage(
      this._member$.pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            this._createAlertRetryWrapper(
              'Loading teams failed',
              signal,
            )(() => this._fetchUserTeams(signal)),
          ).pipe(
            // Without the teams there is no initial team to pick.
            catchError(() => EMPTY),
          ),
        ),
        tap(this._selectInitialTeam),
        whileActive(this._isReady$),
      ),
    )

    this.step = createStep({
      stepId: 'team',
      isCompleted: () => Boolean(this._selectedTeam),
      outlineItemDisplay: () => {
        const title = this._canEditDraftJobsAsAdmin() ? ADMIN_TITLE : TITLE

        if (!this.selectedTeam) {
          return { title }
        }

        if (this.selectedTeam === TeamSelection.myself) {
          if (!this.workspaceMember) {
            return { title }
          }

          return {
            title,
            value: this.nullSelectionPlaceholder,
            visual: {
              type: 'Image',
              url: this.workspaceMember.avatar,
            },
          }
        }

        return {
          title,
          value: this.selectedTeam.name,
          visual: {
            type: 'Image',
            url: this.selectedTeam.avatar,
          },
        }
      },
    })
  }

  /**
   * Label for not picking a team.
   */
  @computed
  get nullSelectionPlaceholder() {
    return this._canEditDraftJobsAsAdmin() ? 'None' : 'Myself'
  }

  /**
   * Whether the user drafts the job as an admin.
   */
  @computed
  get canEditDraftJobsAsAdmin() {
    return this._canEditDraftJobsAsAdmin()
  }

  /**
   * This is the only step accessor.
   */
  @computed
  get stepAccessors() {
    return this.availableTeams.length > 1 ? [this] : []
  }

  /**
   * The selected workspace member.
   */
  @computed
  get workspaceMember() {
    return this._$.member()
  }

  /**
   * The team the user chose, even while it isn't among the available teams.
   */
  @computed
  get chosenTeam(): TeamSelection | null {
    return this._selectedTeam
  }

  /**
   * The currently selected team.
   * Ensures we don't select a team that isn't in the workspace being used.
   * IMPORTANT: This will return `null` if the team members have not
   * been fetched yet.
   */
  @computed
  get selectedTeam(): TeamSelection | null {
    const team = this._selectedTeam
    if (!team) {
      return null
    }

    if (this.availableTeams.includes(team)) {
      return team
    }

    return null
  }

  /**
   * Available teams.
   */
  @computed
  get availableTeams(): TeamSelection[] {
    const member = this.workspaceMember
    if (!member) {
      return [TeamSelection.myself]
    }

    const availableTeamMembersForWorkspace =
      this._getTeamMembersForWorkspaceMember(
        member.workspace.id,
        member.user.id,
      )
        .filter(
          (tm) => tm.permissions.createTeamJobs || tm.permissions.startTeamJobs,
        )
        .map((tm) => tm.team)

    if (member.permissions.startNewJob || member.permissions.adminJobCreate) {
      return [TeamSelection.myself, ...availableTeamMembersForWorkspace]
    }

    return availableTeamMembersForWorkspace
  }

  /**
   * Whether the team selector is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this._isReady = false
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  @action.bound
  ready() {
    this._isReady = this._lifecycle.active
  }

  /**
   * Hydrates the Team Selector state from the Job.
   *
   * @param context
   * @param signal
   */
  async prepare(context: PrepareContext, signal?: AbortSignal) {
    await this._fetchUserTeams(signal)
    signal?.throwIfAborted()
    if (context.type === 'NewDraft') {
      const prefilledTeamId = context.prefilledDraft.teamId
      if (prefilledTeamId === null) {
        // If `null`, it means we explicitly set it to be `myself`
        this.selectTeam(TeamSelection.myself)
      } else if (typeof prefilledTeamId === 'string') {
        // If it's a string then its an explicit team ID.
        this.selectTeam(this._getTeam(prefilledTeamId))
      }

      // If we haven't made a selection by now, use
      // the default behavior.
      this._selectInitialTeam()
    } else {
      this.setTeam(context.job.ownerTeam ?? TeamSelection.myself)
    }
  }

  /**
   * Sets the selected team.
   *
   * @param team
   */
  @action.bound
  setTeam(team: TeamSelection | null) {
    this._selectedTeam = team ?? null
  }

  /**
   * Selects the specified team.
   *
   * @param team
   */
  @action.bound
  selectTeam(team: TeamSelection | undefined | null) {
    team ??= null
    if (team !== this._selectedTeam) {
      this.setTeam(team)
      this._changesSubject.next({ team, type: 'TeamSelected' })
    }
  }

  /**
   * The team the current user belongs to with the given ID, or `Team.none`.
   *
   * @param teamId
   */
  private _getTeam(teamId: Id): Team {
    return (
      this._getTeamMember(teamId, this._getCurrentMember().user.publicId)
        ?.team ?? Team.none
    )
  }

  /**
   * Fetches the user's teams in the selected workspace.
   * There is no paging.
   *
   * @private
   */
  private async _fetchUserTeams(signal?: AbortSignal) {
    const member = this.workspaceMember

    if (!member?.workspace.id) {
      return
    }

    await this._listUserTeamMembersForWorkspace(
      {
        userPublicId: member.user.publicId,
        workspaceId: member.workspace.id,
      },
      signal,
    )
  }

  /**
   * Used to select the initial team, if any.
   * Only happens if we don't have a selection.
   *
   * @private
   */
  @action.bound
  private _selectInitialTeam() {
    // Do nothing if we are read-only (a Job has already been created).
    if (this._isDisabled()) {
      return
    }

    // If we already have a valid team selected,
    // do nothing.
    if (this.selectedTeam) {
      return
    }

    this.selectTeam(TeamSelection.myself)
  }
}

/**
 * Factory for creating a {@link TeamSelectorViewModel}.
 */
export type TeamSelectorViewModelFactory = ReturnType<
  typeof createTeamSelectorViewModelFactory
>

/** @see TeamSelectorViewModelFactory */
export function createTeamSelectorViewModelFactory(
  getCurrentMember: GetCurrentMemberFn,
  getTeamMember: GetTeamMemberFn,
  listUserTeamMembersForWorkspace: ListUserTeamMembersForWorkspaceFn,
  getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
) {
  return (
    createStep: StepFactory,
    member$: Observable<Member | null>,
    isDisabled: () => boolean,
    canEditDraftJobsAsAdmin: () => boolean,
  ) =>
    new TeamSelectorViewModel(
      createStep,
      member$,
      getCurrentMember,
      getTeamMember,
      listUserTeamMembersForWorkspace,
      getTeamMembersForWorkspaceMember,
      createAlertRetryWrapper,
      isDisabled,
      canEditDraftJobsAsAdmin,
    )
}
